/**
 * Ask Atlas floating help widget (ADR-0069).
 *
 * Floating ? button — always visible in tenant shell.
 * Click opens a right-side drawer with AI-powered chat.
 *
 * Session is created lazily on first message send.
 * Custom event `atlas:open-help-widget` opens the drawer from anywhere
 * (used by the HelpDocs "Chat with Ask Atlas" button).
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { apiFetch } from "../lib/api.js";
import { useToast } from "./Toast.js";
import { useAiChatThread, type AiChatMessage } from "../hooks/useAiChatThread.js";
import {
  ChatMessageList,
  ChatComposer,
  ChatThinkingIndicator,
  ChatErrorBanner
} from "./aiChat/index.js";

// ---------------------------------------------------------------------------
// routeToPageName (console-local, spec §6.2 — for context pill display only)
// ---------------------------------------------------------------------------

function routeToPageName(route: string): string {
  if (route === "/app/home" || route === "/app/" || route === "/app") return "Home";
  if (route.startsWith("/app/pages/")) return "Pages";
  if (route === "/app/action-items/new") return "New Action Item";
  if (route.startsWith("/app/action-items/")) return "Action Item Detail";
  if (route === "/app/action-items") return "Action Items";
  if (route === "/app/my-action-items") return "My Action Items";
  if (route === "/app/notifications") return "Notifications";
  if (route === "/app/settings/notifications") return "Notification Settings";
  if (route === "/app/account") return "Account";
  if (route === "/app/help") return "Help & Docs";
  if (route.startsWith("/app/build/providers/")) return "Data Provider Editor";
  if (route === "/app/build/providers") return "Data Providers";
  if (route.startsWith("/app/build/visualizations/")) return "Visualization Editor";
  if (route === "/app/build/visualizations") return "Visualizations";
  if (route.startsWith("/app/build/pages/")) return "Page Editor";
  if (route === "/app/build/pages") return "Page Builder";
  if (route.startsWith("/app/build/menus/")) return "Menu Editor";
  if (route === "/app/build/menus") return "Menu Builder";
  if (route.startsWith("/app/build/operations/")) return "Operation Editor";
  if (route === "/app/build/operations") return "Operations";
  if (route.startsWith("/app/build/semantic-models/")) return "Semantic Designer";
  if (route === "/app/build/semantic-models") return "Semantic Models";
  if (route.startsWith("/app/build/pdos/")) return "PDO Detail";
  if (route === "/app/build/pdos") return "Persistent Data Objects";
  if (route === "/app/build/content-transfer") return "Import / Export";
  if (route === "/app/atlas-query") return "Atlas Query";
  if (route.startsWith("/app/schedule/workflows/")) return "Workflow Canvas";
  if (route === "/app/schedule/workflows") return "Schedule Workflows";
  if (route === "/app/schedule/blackout-schedules") return "Blackout Schedules";
  if (route === "/app/schedule/activity") return "Schedule Activity";
  if (route === "/app/admin/users") return "Users & Groups";
  if (route.startsWith("/app/admin/connectors")) return "Connectors";
  if (route === "/app/admin/ai-usage") return "AI Usage";
  if (route === "/app/admin/ai-knowledge") return "AI Knowledge";
  if (route === "/app/admin/tenant-knowledge") return "Tenant Knowledge";
  if (route === "/app/admin/action-item-codes") return "Action Item Codes";
  if (route === "/app/admin/settings") return "Settings";
  return "Atlas";
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ChatMessage = AiChatMessage;

const GREETING_TEXT = "Hi! I'm Ask Atlas. Ask me anything about how to use this platform.";

// ---------------------------------------------------------------------------
// Icon
// ---------------------------------------------------------------------------

function IconQuestion({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="10" cy="10" r="8" />
      <path d="M7.5 7.5a2.5 2.5 0 0 1 5 0c0 1.5-2.5 2-2.5 3.5" />
      <path d="M10 15h.01" strokeWidth="2.5" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// HelpWidget
// ---------------------------------------------------------------------------

export function HelpWidget() {
  const location = useLocation();
  const toast = useToast();

  const [open, setOpen] = useState(false);
  const {
    sessionId,
    setSessionId,
    messages,
    setMessages,
    replyPending,
    error: chatThreadError,
    resetChat,
    cancelChat,
  } = useAiChatThread<ChatMessage>({
    threadKey: "help-widget",
    disabled: !open,
    loadLatest: useCallback(async () => {
      const res = await apiFetch<{
        session: { id: string; created_at?: string };
        messages: Array<{ id: string; role: string; content: string; created_at?: string }>;
      }>("/api/tenant/help/sessions/latest");
      return {
        sessionId: res.session.id,
        messages: res.messages.map((m) => ({
          id: m.id,
          role: m.role as ChatMessage["role"],
          content: m.content,
          created_at: m.created_at ?? "",
        })),
      };
    }, []),
    createNewSession: useCallback(async () => {
      const res = await apiFetch<{ session_id: string }>(
        "/api/tenant/help/sessions",
        { method: "POST", body: {} }
      );
      return { sessionId: res.session_id };
    }, []),
    cancelChat: useCallback(async (sid: string) => {
      await apiFetch(
        `/api/tenant/ai/sessions/${encodeURIComponent(sid)}/abort`,
        { method: "POST", body: {} }
      );
    }, []),
  });
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const listRef = useRef<HTMLDivElement | null>(null);

  // Open via custom event (from HelpDocs "Chat with Ask Atlas" button).
  useEffect(() => {
    function handler() { setOpen(true); }
    document.addEventListener("atlas:open-help-widget", handler);
    return () => document.removeEventListener("atlas:open-help-widget", handler);
  }, []);

  // Scroll to bottom when messages change.
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages]);

  async function send() {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);

    const optimisticId = `local-${Date.now()}`;
    setMessages((prev) => [...prev, { id: optimisticId, role: "user", content: text, created_at: new Date().toISOString() }]);

    try {
      // Lazy session creation.
      let sid = sessionId;
      if (!sid) {
        const res = await apiFetch<{ session_id: string }>("/api/tenant/help/sessions", { method: "POST", body: {} });
        sid = res.session_id;
        setSessionId(sid);
      }

      const reply = await apiFetch<{ role: string; content: string }>(
        `/api/tenant/help/sessions/${sid}/messages`,
        { method: "POST", body: { content: text, current_route: location.pathname } }
      );
      setMessages((prev) => [...prev, { id: `local-asst-${Date.now()}`, role: "assistant", content: reply.content, created_at: new Date().toISOString() }]);
    } catch (e) {
      // Check for 503 no_anthropic_model.
      const msg = e instanceof Error ? e.message : String(e);
      if (msg.includes("help_model_unavailable") || msg.includes("no_anthropic_model")) {
        setError("Ask Atlas requires an Anthropic model. Ask your administrator to configure one under Admin → Settings.");
      } else {
        toast.push({ kind: "error", message: msg });
        // Remove the optimistic user message on error.
        setMessages((prev) => prev.filter((m) => m.id !== optimisticId));
      }
    } finally {
      setBusy(false);
    }
  }

  async function resetSession() {
    setError(null);
    setInput("");
    await resetChat();
  }

  const pageName = routeToPageName(location.pathname);

  return (
    <>
      {/* Topbar trigger button */}
      <button
        type="button"
        className="menuButton"
        aria-label={open ? "Close Ask Atlas help" : "Open Ask Atlas help"}
        title="Ask Atlas help"
        onClick={() => setOpen((v) => !v)}
      >
        <IconQuestion size={18} />
      </button>

      {/* Drawer */}
      {open && (
        <aside className="helpDrawer" role="dialog" aria-modal="true" aria-label="Ask Atlas help">
          {/* Header */}
          <header className="helpDrawerHeader">
            <span style={{ fontWeight: 700 }}>Ask Atlas</span>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Link to="/app/help" className="muted" style={{ fontSize: 13 }}>Full docs →</Link>
              <button type="button" className="btn" onClick={() => setOpen(false)} aria-label="Close">×</button>
            </div>
          </header>

          {/* Context pill */}
          <div className="helpContextPill">Current page: {pageName}</div>

          {/* Message list */}
          <div className="helpChatList" ref={listRef}>
            <ChatMessageList
              messages={messages}
              assistantLabel="Ask Atlas"
              emptyState={
                <div className="helpChatMsg assistant">{GREETING_TEXT}</div>
              }
              trailingNode={<ChatThinkingIndicator visible={busy || replyPending} />}
            />
          </div>

          {/* Error state or input */}
          <ChatErrorBanner message={error ?? chatThreadError} />
          {!error ? (
            <div className="helpChatInputRow">
              <ChatComposer
                draft={input}
                setDraft={setInput}
                onSend={() => void send()}
                onCancel={() => void cancelChat()}
                inFlight={busy || replyPending}
                placeholder="Ask anything…"
                minRows={1}
                maxRows={3}
              />
            </div>
          ) : null}

          {/* Footer */}
          <div style={{ padding: "8px 16px", borderTop: "1px solid var(--border)", display: "flex", justifyContent: "flex-end" }}>
            <button type="button" className="btn" style={{ fontSize: 12 }} onClick={resetSession}>
              Start new conversation
            </button>
          </div>
        </aside>
      )}
    </>
  );
}
